import React, { useEffect, useState } from "react";
import escapeHtml from "lodash/escape";
import Swal from "../../utils/swal";
import apiClient from "../../utils/apiClient";
import Grid from "@mui/material/Grid";
import CustomButton from "../custom/CustomButton";
import { useUser } from "../../pages/UserContext";
import RowActionsMenu from "../custom/RowActionsMenu";
import CustomModal from "../custom/CustomModal";
import CustomDataGrid from "../custom/CustomDataGrid";
import CustomTextField from "../custom/CustomTextField";
import { Typography, Box } from "@mui/material";
import EditPublikasiModal from "./EditPublikasiModal";
import InsertPublikasiModal from "./InsertPublikasiModal";
import { showSuccessToast, showErrorToast, showWarningToast } from "../../utils/swalToast";

export default function JournalData() {
  const userDetails = useUser();
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [googleScholarId, setGoogleScholarId] = useState("");
  const [currentGoogleScholarId, setCurrentGoogleScholarId] = useState("");
  const [refresh, setRefresh] = useState(false);

  const [openInsertModal, setOpenInsertModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editTarget, setEditTarget] = useState({ userId: null, journalId: null });

  // set open modal for sync gscholar
  const [modalOpen, setModalOpen] = useState(false);

  // set open modal for edit gscholar
  const [editGscholarModalOpen, setEditGscholarModalOpen] = useState(false);

  const [scholarData, setScholarData] = useState([]);
  const [totalData, setTotalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // modal untuk sync data google scholar
  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  // modal untuk update data google scholar id secara langsung
  const handleEditGscholarOpenModal = () => {
    setGoogleScholarId(googleScholarId || "");
    setCurrentGoogleScholarId(currentGoogleScholarId || "");
    setEditGscholarModalOpen(true);
  };
  const handleEditGscholarCloseModal = () => setEditGscholarModalOpen(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get("/users/details/all");
        setGoogleScholarId(response.data.data.google_scholar_id || "");
        setCurrentGoogleScholarId(response.data.data.google_scholar_id || "");
      } catch (error) {
        alert("Error fetching user details", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let url = "/journal?isDeleted=0";
        if (userDetails.role !== "admin") {
          url += `&lecturerId=${userDetails.uuid}`;
        }

        const response = await apiClient.get(url);

        setRows(
          response.data.data.map((journal, index) => ({
            no: index + 1 + (page - 1) * 10,
            id: journal.id,
            idDosen: journal.id_dosen,
            namaAnggota: journal.lecturer_name,
            judulPublikasi: journal.judul,
            namaPublikasi: journal.nama_jurnal,
          }))
        );
      } catch (error) {
        console.error("Error fetching journals", error);
      } finally {
        setTableLoading(false);
      }
    };

    fetchData();
  }, [page, refresh, userDetails.role, userDetails.uuid]);

  const handleAddJournal = () => {
    setOpenInsertModal(true);
  };

  const handleInsertSuccess = () => {
    setOpenInsertModal(false);
    setRefresh((prev) => !prev);
  };

  const handleEditJournal = (userId, journalId) => {
    setEditTarget({ userId, journalId });
    setOpenEditModal(true);
  };

  const handleEditSuccess = () => {
    setOpenEditModal(false);
    setRefresh((prev) => !prev);
  };

  const handleDeleteJournal = async (journalId) => {
    Swal.fire({
      text: "Kamu akan menghapus publikasi ini",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#003158",
      cancelButtonColor: "#A92027",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await apiClient.post("/journal/delete", { id: journalId });
          showSuccessToast(response.data.msg);
          setRefresh((prev) => !prev);
        } catch (error) {
          console.error("Error deleting journal", error);
          showErrorToast("There was an error deleting journal.");
        }
      }
    });
  };

  const handleSyncGoogleScholarData = async () => {
    setLoading(true);
    setScholarData(null);
    setError("");
    handleOpenModal();

    if (!googleScholarId) {
      setError(
        "Google Scholar ID-mu masih kosong, tolong isi terlebih dahulu di halaman profil."
      );
      setLoading(false);
      return;
    }

    try {
      const response = await apiClient.get(
        `/journal/scholar?scholarId=${googleScholarId}`
      );

      const data = response.data.data;

      if (!data || data.length === 0) {
        setError("Tidak ada publikasi yang ditemukan dari Google Scholar.");
        setScholarData([]);
      } else {
        setScholarData(data);
      }

      setTotalData(data.length || 0);
      setLoading(false);
    } catch (error) {
      console.error("Error syncing Google Scholar data", error);
      setError("Terjadi kesalahan saat mengambil data dari Google Scholar.");
      setLoading(false);
    }
  };

  // const handleSyncGoogleScholarData = async () => {
  //   setLoading(true);
  //   setScholarData(null);
  //   setError("");
  //   handleOpenModal();

  //   try {
  //     const response = await fetch(
  //       `https://gscholarapi-production.up.railway.app/get-journal?scholarId=${googleScholarId}`
  //     );

  //     console.log("response ", response);

  //     if (!response.ok) {
  //       const errorData = await response.json();
  //       throw new Error(
  //         errorData.message || "Gagal mengambil data dari Google Scholar"
  //       );
  //     }

  //     const data = await response.json();
  //     setScholarData(data.data);
  //     setTotalData(data.total); // Kalau `total` memang disediakan oleh API
  //     setLoading(false);
  //   } catch (error) {
  //     console.error("Error syncing Google Scholar data", error);
  //     setError("There was an error syncing Google Scholar data.");
  //     setLoading(false);
  //   }
  // };

  const handleOpenGoogleScholarEditModal = async () => {
    handleEditGscholarOpenModal();
  };

  // Handle Update Google Scholar ID
  const handleUpdateGoogleScholarId = async () => {
    // Check if Google Scholar ID is empty
    if (!googleScholarId) {
      showWarningToast("Google Scholar ID tidak boleh kosong!");
      return;
    }

    // Confirmation swal for updating Google Scholar ID
    const result = await Swal.fire({
      title: "Pembaharuan Data",
      html: `Google Scholar ID Anda akan diperbaharui menjadi <span style="color: #d32f2f; font-weight: bold;">${escapeHtml(googleScholarId)}</span>, apakah Anda setuju?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#003158",
      cancelButtonColor: "#A92027",
      confirmButtonText: "Ya",
      cancelButtonText: "Batal",
      didOpen: () => {
        const swalContainer = document.querySelector(".swal2-container");
        if (swalContainer) {
          swalContainer.style.zIndex = "2000";
        }
      },
    });

    // If user confirms the update
    if (result.isConfirmed) {
      try {
        const res = await apiClient.put("/users/gscholar", {
          googleScholarId,
        });

        showSuccessToast(res.data.message || "Google Scholar ID berhasil diperbarui.");
        setCurrentGoogleScholarId(googleScholarId);
        handleEditGscholarCloseModal();
      } catch (error) {
        showErrorToast(
          error.response?.data?.message ||
            "Terjadi kesalahan saat memperbarui Google Scholar ID."
        );
      }
    }
  };

  const handleSyncDataButtonClick = async () => {
    try {
      const journalsToSync = scholarData.map((item) => ({
        title: item.title,
        journalName: item.publication,
      }));
      const response = await apiClient.post("/journal/scholar", {
        journals: journalsToSync,
      });
      setModalOpen(false);
      showSuccessToast(response.data.msg);
      setRefresh((prev) => !prev);
    } catch (error) {
      setModalOpen(false);
      showErrorToast(error.response.data.msg);
    }
  };

  const columns = [
    { field: "no", headerName: "No.", flex: 1 },
    { field: "namaAnggota", headerName: "Nama Anggota", flex: 1 },
    { field: "judulPublikasi", headerName: "Judul Publikasi", flex: 1 },
    { field: "namaPublikasi", headerName: "Nama Publikasi", flex: 1 },
    {
      field: "actions",
      headerName: "Action",
      renderCell: (params) => (
        <RowActionsMenu
          actions={[
            {
              label: "Edit",
              onClick: () => handleEditJournal(params.row.idDosen, params.row.id),
            },
            {
              label: "Hapus",
              onClick: () => handleDeleteJournal(params.row.id),
              danger: true,
            },
          ]}
        />
      ),
      flex: 1,
    },
  ];

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <div style={{ marginBottom: "10px" }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            mb: 2,
            borderRadius: "8px",
            backgroundColor: "#f5f7fa",
            border: "1px dashed #003158",
          }}
        >
          <Typography sx={{ fontSize: "0.95em", color: "#555" }}>
            Google Scholar ID:
          </Typography>
          <Typography sx={{ fontSize: "0.95em", fontWeight: 700, color: "#003158" }}>
            {currentGoogleScholarId || "Belum ada Google Scholar ID"}
          </Typography>
        </Box>

        <div
          style={{ marginBottom: "10px", fontSize: "16px", fontWeight: "bold" }}
        >
          {/* <Grid container spacing={2}>
            <Grid item md={3} xs={12}>
              <CustomTextField
                value={googleScholarId}
                onChange={(event) => handleGoogleScholarIdChange()}
              />
            </Grid>
          </Grid> */}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
          <CustomButton
            onClick={handleSyncGoogleScholarData}
            text="Google Scholar Sync"
            isFullWidth={false}
            baseColor="#003158"
            hoverColor="#00213A"
          />
          <CustomButton
            onClick={handleAddJournal}
            text="Tambah Publikasi"
            isFullWidth={false}
            baseColor="#003158"
            hoverColor="#00213A"
          />
          <CustomButton
            onClick={handleOpenGoogleScholarEditModal}
            text="Update Google Scholar Id"
            isFullWidth={false}
            baseColor="#003158"
            hoverColor="#00213A"
          />
        </div>
      </div>

      <br />

      <CustomDataGrid
        rows={rows}
        columns={columns}
        setPage={setPage}
        loading={tableLoading}
      />

      <EditPublikasiModal
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        userId={editTarget.userId}
        journalId={editTarget.journalId}
        onSuccess={handleEditSuccess}
      />

      <InsertPublikasiModal
        open={openInsertModal}
        onClose={() => setOpenInsertModal(false)}
        onSuccess={handleInsertSuccess}
      />

      <CustomModal
        open={modalOpen}
        onClose={handleCloseModal}
        title="Google Scholar Data"
        loading={loading}
        error={error}
        data={scholarData}
        columns={[
          { field: "title", headerName: "Title" },
          { field: "year", headerName: "Year" },
          { field: "authors", headerName: "Authors" },
          { field: "publication", headerName: "Publication" },
          {
            field: "url",
            headerName: "Link",
            render: (value) => (
              <a href={value} target="_blank" rel="noopener noreferrer">
                View
              </a>
            ),
          },
        ]}
        onSync={handleSyncDataButtonClick}
        showButton={true}
      />

      <CustomModal
        open={editGscholarModalOpen}
        onClose={handleEditGscholarCloseModal}
        title="Edit Google Scholar ID"
        showButton={false}
        customContent={
          <div>
            <Typography
              variant="body2"
              color="error"
              sx={{ fontWeight: "bold", marginBottom: "20px" }}
            >
              * Note: Untuk mendapatkan Google Scholar ID, silakan buka profil
              Google Scholar Anda melalui link berikut: <br></br>
              <a
                href="https://scholar.google.com/citations"
                target="_blank"
                rel="noopener noreferrer"
              >
                Link Profile Google Scholar
              </a>
              . Setelah halaman terbuka, klik profil Anda lalu salin bagian
              setelah "user" pada URL,
              <br></br>
              contoh :
              <em>https://scholar.google.com/citations?user=Nyh5hh0AAAAJ</em>.
              Kemudian masukkan bagian <u>Nyh5hh0AAAAJ</u> ke dalam input
              berikut.
            </Typography>

            <Grid container spacing={2}>
              <Grid item md={10} xs={6}>
                <CustomTextField
                  label="Google Scholar Id"
                  placeholder="Contoh: P9xYzP0AAAAJ"
                  value={googleScholarId}
                  onChange={(event) => setGoogleScholarId(event.target.value)}
                />
              </Grid>
              <Grid item md={2} xs={6}>
                <CustomButton
                  text="Submit"
                  baseColor="#003158"
                  hoverColor="#00213A"
                  onClick={handleUpdateGoogleScholarId}
                  disabled={
                    googleScholarId.trim() === currentGoogleScholarId?.trim()
                  }
                />
              </Grid>
            </Grid>
          </div>
        }
      />
    </div>
  );
}
